/**
 * Bug Reports Admin Page
 *
 * Lists all bug reports with filtering and allows reviewing/exporting to Linear
 */
import { useState, useEffect } from "react";
import { useSnapshot } from "valtio";
import {
  adminStore,
  fetchBugReports,
  type BugReport,
} from "@/features/admin/stores/admin";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { BugReviewModal } from "./BugReviewModal";
import { ExternalLink, Bug } from "lucide-react";

type Filter = "all" | "pending" | "exported";

export default function BugReports() {
  const [filter, setFilter] = useState<Filter>("pending");
  const [selectedBugId, setSelectedBugId] = useState<string | null>(null);

  const { bugReports, bugReportsLoading } = useSnapshot(adminStore);

  useEffect(() => {
    fetchBugReports();
  }, []);

  const bugs = bugReports as BugReport[];

  const filteredBugs = bugs
    .filter((bug) => {
      if (filter === "pending") return !bug.exportedAt;
      if (filter === "exported") return !!bug.exportedAt;
      return true;
    })
    .sort((a, b) => b.timestamp - a.timestamp);

  const selectedBug = bugs.find((b) => b.id === selectedBugId);

  const counts = {
    all: bugs.length,
    pending: bugs.filter((b) => !b.exportedAt).length,
    exported: bugs.filter((b) => !!b.exportedAt).length,
  };

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <header className="shrink-0 border-b bg-background px-4 py-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <div className="flex items-center gap-3">
            <Bug className="h-8 w-8 text-violet-600" />
            <div>
              <h1 className="text-3xl font-bold">Bug Reports</h1>
              <p className="mt-1 text-muted-foreground">
                Review user-submitted bug reports and export to Linear
              </p>
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="min-h-0 flex-1 overflow-y-auto">
        <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
          {/* Filter tabs */}
          <div className="mb-6 flex gap-2">
            {(["pending", "exported", "all"] as const).map((f) => (
              <Button
                key={f}
                variant={filter === f ? "default" : "outline"}
                size="sm"
                onClick={() => setFilter(f)}
              >
                {f.charAt(0).toUpperCase() + f.slice(1)}
                <Badge variant="secondary" className="ml-2">
                  {counts[f]}
                </Badge>
              </Button>
            ))}
          </div>

          {/* Bug list */}
          {bugReportsLoading ? (
            <div className="grid gap-4">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-24" />
              ))}
            </div>
          ) : (
            <div className="grid gap-4">
              {filteredBugs.map((bug) => (
                <Card
                  key={bug.id}
                  className="cursor-pointer p-4 transition-colors hover:bg-muted/50"
                  onClick={() => setSelectedBugId(bug.id)}
                >
                  <div className="flex items-start gap-4">
                    {/* Thumbnail */}
                    <img
                      src={bug.annotatedScreenshotUrl}
                      alt="Screenshot"
                      className="h-20 w-32 rounded border object-cover"
                    />

                    {/* Content */}
                    <div className="min-w-0 flex-1">
                      <div className="mb-1 flex items-center gap-2">
                        <h3 className="truncate font-medium">{bug.title}</h3>
                        {bug.linearIssueIdentifier && (
                          <a
                            href={bug.linearIssueUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Badge variant="outline">
                              {bug.linearIssueIdentifier}
                            </Badge>
                            <ExternalLink className="h-3 w-3" />
                          </a>
                        )}
                      </div>
                      <p className="line-clamp-2 text-sm text-muted-foreground">
                        {bug.description || "No description provided"}
                      </p>
                      <div className="mt-2 flex items-center gap-4 text-xs text-muted-foreground">
                        <span>{new Date(bug.timestamp).toLocaleString()}</span>
                        <span className="truncate">{bug.pageUrl}</span>
                      </div>
                    </div>

                    {/* Status */}
                    <Badge variant={bug.exportedAt ? "default" : "secondary"}>
                      {bug.exportedAt ? "Exported" : "Pending"}
                    </Badge>
                  </div>
                </Card>
              ))}

              {filteredBugs.length === 0 && (
                <div className="py-12 text-center text-muted-foreground">
                  No bug reports found
                </div>
              )}
            </div>
          )}
        </div>
      </main>

      {/* Review modal */}
      {selectedBug && (
        <BugReviewModal
          bug={selectedBug}
          onClose={() => setSelectedBugId(null)}
        />
      )}
    </div>
  );
}
